import { useElementSize } from '@mantine/hooks';
import { type JSX, useMemo } from 'react';
import { useColorBlindModeEnabled } from 'ts/base/hooks/ColorBlindModeHook';
import * as Style from 'ts/perspectives/compare/Style';
import type { DiffDescription } from 'typedefs/DiffDescription';

type DiffIndicatorElement = {
	percentageStartY: number;
	percentageEndY: number;
	lineNumber: number;
	fillStyle?: string;
	strokeStyle?: string;
};

function createDiffIndicatorElements(
	numberOfLines: number,
	changeLines: number[],
	currentDiff: DiffDescription,
	colorBlindModeEnabled: boolean
) {
	if (numberOfLines <= 0) {
		return [];
	}

	const diffIndicatorElements: DiffIndicatorElement[] = [];

	for (let i = 0; i < changeLines.length; i += 2) {
		const percentageStartY = changeLines[i]! / numberOfLines;
		const percentageEndY = changeLines[i + 1]! / numberOfLines;
		const style = getChangeStyle(i, currentDiff, colorBlindModeEnabled);

		diffIndicatorElements.push({
			percentageStartY,
			percentageEndY,
			lineNumber: changeLines[i]!,
			fillStyle: style.background,
			strokeStyle: style.color
		});
	}

	return diffIndicatorElements;
}

type CodeDiffOutlineBarProps = {
	className: string;
	changeLines: number[];
	currentDiff: DiffDescription;
	numberOfLines: number;
	onDiffClick?: (lineNumber: number) => void;
};

/** Visualizes an overview over all change regions in the code element. */
export function CodeDiffOutlineBar({
	className,
	changeLines,
	currentDiff,
	numberOfLines,
	onDiffClick
}: CodeDiffOutlineBarProps): JSX.Element {
	const colorBlindModeEnabled = useColorBlindModeEnabled();
	const { ref, width, height } = useElementSize();

	const diffIndicators = useMemo(
		() => createDiffIndicatorElements(numberOfLines, changeLines, currentDiff, colorBlindModeEnabled),
		[numberOfLines, changeLines, currentDiff, colorBlindModeEnabled]
	);

	return (
		<div ref={ref} className={className}>
			<svg width={width} height={height} className="absolute inset-0">
				{/* Background */}
				<rect x="0" y="0" width="100%" height="100%" fill="#eee" />

				{/* Diff Indicators */}
				{diffIndicators.map((indicator, index) => (
					<rect
						key={`diff-${index}`}
						x={0}
						y={indicator.percentageStartY * height}
						width={width}
						height={(indicator.percentageEndY - indicator.percentageStartY) * height}
						fill={indicator.fillStyle}
						stroke={indicator.strokeStyle}
						className="cursor-pointer"
						onClick={() => onDiffClick?.(indicator.lineNumber)}
					/>
				))}
			</svg>
		</div>
	);
}

/** Returns the colors used to visualize a code change (background and border color). */
export function getChangeStyle(changeIndex: number, currentDiff: DiffDescription, colorBlindModeEnabled: boolean) {
	const hasLeftChanges = currentDiff.leftChangeLines[changeIndex]! < currentDiff.leftChangeLines[changeIndex + 1]!;
	const hasRightChanges = currentDiff.rightChangeLines[changeIndex]! < currentDiff.rightChangeLines[changeIndex + 1]!;

	if (hasLeftChanges && hasRightChanges) {
		// Change
		return colorBlindModeEnabled ? Style.DIFF_BLUE_COLOR_BLIND_MODE : Style.DIFF_BLUE;
	} else if (hasLeftChanges) {
		// Remove
		return colorBlindModeEnabled ? Style.DIFF_YELLOW : Style.DIFF_RED;
	} else {
		// Add
		return colorBlindModeEnabled ? Style.DIFF_GREEN_COLOR_BLIND_MODE : Style.DIFF_GREEN;
	}
}
